import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface ToolParameter {
  id: number;
  name: string;
  type: string;
  description: string | null;
  default_description: string | null;
  custom_description: string | null;
  required: boolean;
}

export interface Tool {
  id: number;
  identifier: string;
  label: string;
  display_name: string;
  description: string | null;
  agent_prompt_support: string | null;
  enabled: boolean;
  tool_type: string;
  agent_id: number;
  agent_label: string | null;
  parameters?: ToolParameter[];
  created_at: string;
  updated_at: string;
}

export interface ToolParameterUpdateInput {
  id: number;
  description?: string;
}

export interface ToolUpdateInput {
  description?: string;
  display_name?: string;
  agent_prompt_support?: string;
  parameters_attributes?: ToolParameterUpdateInput[];
}

const BASE_URL = "/system/agentic/tools";

export const toolApi = {
  list: async (): Promise<Tool[]> => {
    const { data } = await axios.get(`${BASE_URL}.json`);
    return data;
  },

  get: async (id: number): Promise<Tool> => {
    const { data } = await axios.get(`${BASE_URL}/${id}.json`);
    return data;
  },

  update: async (id: number, input: ToolUpdateInput): Promise<Tool> => {
    const { data } = await axios.patch(`${BASE_URL}/${id}.json`, {
      tool: input,
    });
    return data;
  },
};

export const toolKeys = {
  all: ["tools"] as const,
  detail: (id: number) => ["tools", id] as const,
};

export function useTools() {
  return useQuery({
    queryKey: toolKeys.all,
    queryFn: toolApi.list,
  });
}

export function useTool(id: number) {
  return useQuery({
    queryKey: toolKeys.detail(id),
    queryFn: () => toolApi.get(id),
    enabled: !!id,
  });
}

export function useUpdateTool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: ToolUpdateInput }) =>
      toolApi.update(id, input),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: toolKeys.all });
      queryClient.invalidateQueries({ queryKey: toolKeys.detail(id) });
    },
  });
}
